import type { CSSProperties } from "react";
import type { FrameMeta, SlotShape } from "../../lib/frameDetect";

/** Stand-in profile picture for the preview (no network needed). */
export const SAMPLE_DP =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400'><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='#ffb86b'/><stop offset='1' stop-color='#c04ad8'/></linearGradient></defs><rect width='400' height='400' fill='url(#g)'/><circle cx='200' cy='160' r='72' fill='#fff' opacity='.9'/><path d='M60 400c0-86 62-140 140-140s140 54 140 140z' fill='#fff' opacity='.9'/><path d='M0 0h400v400H0z' fill='none' stroke='#000' stroke-opacity='.25' stroke-width='6'/><path d='M200 0v400M0 200h400' stroke='#000' stroke-opacity='.15'/></svg>`
  );

export function shapeRadius(shape: SlotShape, w: number, h: number): string {
  if (shape === "square") return "0";
  if (shape === "rounded") return `${Math.min(w, h) * 0.2}px`;
  return "50%";   // circle + oval
}

/**
 * The frame exactly as the app draws it: the avatar sits BEHIND the frame image, clipped to its opening's shape, and every box is
 * placed with one uniform scale (width / canvasW). Used for the editor stage and for the multi-size preview, so what the admin
 * sees is what AvatarFrameView does.
 */
export function FramePreview({ meta, frameUrl, width, dpUrls, showLabels = true, children }: {
  meta: Pick<FrameMeta, "canvasW" | "canvasH" | "openings" | "labels">;
  frameUrl: string;
  width: number;
  dpUrls?: string[];
  showLabels?: boolean;
  children?: React.ReactNode;     // editor overlay, drawn on top of everything
}) {
  const s = width / meta.canvasW;
  const height = Math.round(meta.canvasH * s);
  return (
    <div style={{ position: "relative", width, height, overflow: "hidden" }} className="select-none">
      {meta.openings.map((o, i) => (
        <img key={o.id} alt="" draggable={false} src={(dpUrls && dpUrls[i]) || SAMPLE_DP}
          style={{ position: "absolute", objectFit: "cover", left: (o.cx - o.w / 2) * s, top: (o.cy - o.h / 2) * s, width: o.w * s, height: o.h * s, borderRadius: shapeRadius(o.shape, o.w, o.h) }} />
      ))}
      <img alt="" draggable={false} src={frameUrl} style={{ position: "absolute", left: 0, top: 0, width, height }} />
      {showLabels && meta.labels.map((l) => (
        <div key={l.id} style={labelStyle(l, s)}>{l.id}</div>
      ))}
      {children}
    </div>
  );
}

function labelStyle(l: { cx: number; cy: number; w: number; h: number }, s: number): CSSProperties {
  return { position: "absolute", left: (l.cx - l.w / 2) * s, top: (l.cy - l.h / 2) * s, width: l.w * s, height: l.h * s, display: "flex", alignItems: "center", justifyContent: "center",
    color: "#fff", fontWeight: 700, fontSize: Math.max(7, Math.min(16, l.h * s * 0.6)), textShadow: "0 1px 3px rgba(0,0,0,.7)", overflow: "hidden", whiteSpace: "nowrap" };
}
